import { Children, forwardRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import classNames from 'classnames';
import {
  Calendar as CalendarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@vgno/icons';

import { formatCalendarDate } from '../../utils/datetime';
import { Button } from '../Button/Button';
import { useControlled } from '../utils/useControlled';

import type { DetailedHTMLProps, HTMLAttributes } from 'react';

import * as styles from './Timetable.module.scss';

export interface TimetableProps
  extends Omit<
    DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
    'onChange'
  > {
  date?: string;
  minDate?: string;
  maxDate?: string;
  onChange?: (date: string) => void;
  className?: string;
  variant?: 'list' | 'sidebar';
  maxEntries?: number;
  sidebarCollapsed?: boolean;
  onSidebarCollapse?: (collapsed: boolean) => void;
  listExpanded?: boolean;
  onListExpand?: (expanded: boolean) => void;
}

const Timetable = forwardRef<HTMLDivElement, TimetableProps>(
  (
    {
      children,
      date,
      maxDate,
      minDate,
      onChange,
      className,
      variant = 'list',
      maxEntries = 10,
      sidebarCollapsed: sidebarCollapsedProp,
      onSidebarCollapse,
      listExpanded: listExpandedProp,
      onListExpand,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();

    const [listExpanded, setListExpanded] = useControlled({
      controlled: listExpandedProp,
      default: false,
      name: 'Timetable',
      state: 'listExpanded',
    });

    const [sidebarCollapsed, setSidebarCollapsed] = useControlled({
      controlled: sidebarCollapsedProp,
      default: false,
      name: 'Timetable',
      state: 'sidebarCollapsed',
    });

    const toggleListExpand = useCallback(() => {
      setListExpanded(!listExpanded);

      if (onListExpand) {
        onListExpand(!listExpanded);
      }
    }, [setListExpanded, listExpanded, onListExpand]);

    const toggleSidebarCollapse = useCallback(() => {
      setSidebarCollapsed(!sidebarCollapsed);

      if (onSidebarCollapse) {
        onSidebarCollapse(!sidebarCollapsed);
      }
    }, [setSidebarCollapsed, sidebarCollapsed, onSidebarCollapse]);

    const handlePrev = useCallback(() => {
      if (onChange) {
        onChange(dayjs(date).subtract(1, 'day').format('YYYY-MM-DD'));
      }
    }, [date, onChange]);

    const handleNext = useCallback(() => {
      if (onChange) {
        onChange(dayjs(date).add(1, 'day').format('YYYY-MM-DD'));
      }
    }, [date, onChange]);

    return (
      <div
        className={classNames(
          styles.list,
          {
            [styles.variantSidebar]: variant === 'sidebar',
            [styles.collapsed]: sidebarCollapsed,
          },
          className
        )}
        {...props}
        ref={ref}
      >
        <div className={styles.wrapper}>
          <div className={styles.header}>
            <div className={styles.title}>{t('Broadcast schedule')}</div>
            {date && (
              <div className={styles.date}>
                <Button
                  variant="text"
                  aria-label={t('previous day')}
                  className={styles.iconButton}
                  disabled={date === minDate}
                  onClick={handlePrev}
                >
                  <ChevronLeftIcon />
                </Button>
                <div className={styles.calendar}>
                  <CalendarIcon />
                  <span className={styles.calendarLabel}>
                    {formatCalendarDate(date)}
                  </span>
                </div>
                <Button
                  variant="text"
                  aria-label={t('next day')}
                  className={styles.iconButton}
                  disabled={date === maxDate}
                  onClick={handleNext}
                >
                  <ChevronRightIcon />
                </Button>
              </div>
            )}
          </div>
          {variant === 'sidebar' || listExpanded
            ? children
            : Children.toArray(children).slice(0, maxEntries)}
          {variant === 'list' &&
            Children.toArray(children).length > maxEntries && (
              <Button
                variant="blurred"
                className={styles.expandButton}
                onClick={toggleListExpand}
              >
                <strong>{t(listExpanded ? 'show less' : 'show more')}</strong>
              </Button>
            )}
        </div>
        {variant === 'sidebar' && (
          <Button
            variant="text"
            className={classNames(styles.iconButton, styles.collapseButton)}
            onClick={toggleSidebarCollapse}
          >
            {sidebarCollapsed ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </Button>
        )}
      </div>
    );
  }
);
Timetable.displayName = 'Timetable';

export { Timetable };
